import type { ReactNode } from 'react';
import { Anchor } from '../primitives/Anchor.js';
import { Badge } from '../primitives/Badge.js';
import { Group } from '../primitives/Group.js';
import { Paper } from '../primitives/Paper.js';
import { Text } from '../primitives/Text.js';

export type ReferenceChipProps = {
  typeLabel: string;
  title: string;
  subtitle?: string | null;
  href?: string | null;
  imageUrl?: string | null;
  /** Leading glyph when no image (entity archetype). */
  icon?: ReactNode;
  /** Replaces default Badge — e.g. colored EntityTypeMark. */
  typeSlot?: ReactNode;
  /** Optional leading well (toned) when no image. */
  leading?: ReactNode;
};

export function ReferenceChip({
  typeLabel,
  title,
  subtitle,
  href,
  imageUrl,
  icon,
  typeSlot,
  leading,
}: ReferenceChipProps) {
  const lead = imageUrl ? (
    <img
      src={imageUrl}
      alt=""
      aria-hidden
      style={{
        width: 40,
        height: 40,
        objectFit: 'cover',
        borderRadius: 10,
        flexShrink: 0,
        outline: '1px solid rgba(0, 0, 0, 0.1)',
      }}
    />
  ) : (
    leading ??
    (icon ? (
      <span
        aria-hidden
        style={{
          width: 40,
          height: 40,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          borderRadius: 10,
          background: 'color-mix(in srgb, var(--wp-brunswick-800, #631d1d) 8%, transparent)',
        }}
      >
        {icon}
      </span>
    ) : null)
  );

  const content = (
    <Paper
      withBorder
      p="xs"
      radius="md"
      style={{ minWidth: 0, background: 'var(--wp-surface-0)', borderColor: 'rgba(25, 32, 56, 0.12)' }}
    >
      <Group align="flex-start" gap="xs" wrap="nowrap">
        {lead}
        <div style={{ minWidth: 0, flex: 1 }}>
          <Group justify="space-between" gap="xs" wrap="nowrap" style={{ marginBottom: subtitle ? 4 : 0 }}>
            {typeSlot ?? <Badge tone="neutral">{typeLabel}</Badge>}
          </Group>
          <Text size="sm" fw={600} style={{ wordBreak: 'break-word' }}>
            {title}
          </Text>
          {subtitle ? (
            <Text size="xs" c="dimmed" style={{ marginTop: 2, wordBreak: 'break-word' }}>
              {subtitle}
            </Text>
          ) : null}
        </div>
      </Group>
    </Paper>
  );

  if (!href) return content;
  return (
    <Anchor href={href} style={{ textDecoration: 'none', color: 'inherit' }}>
      {content}
    </Anchor>
  );
}
